import { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Box, Typography, Tooltip, IconButton, TextField, Button } from '@mui/material'
import { InfoOutlined as InfoOutlinedIcon, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material'
import { Tag, FilterDropdown } from '../filterComponents'
import { isEqual } from 'lodash'
import { LinkedIn } from 'src/icons/linkedin'
import { useTheme } from '@emotion/react'

export default function LinkedInFilter ({ onChange, type, isOpen, onToggle }) {
  const theme = useTheme()
  const [includedProfiles, setIncludedProfiles] = useState([])
  const [excludedProfiles, setExcludedProfiles] = useState([])
  const [manualInclude, setManualInclude] = useState('')
  const [manualExclude, setManualExclude] = useState('')
  const ref = useRef({ internalStateChange: false })

  const linkedinData = useSelector((state) => state.search[type === 'company' ? 'companyFilter' : 'filter'].linkedin)
  const excludeLinkedinData = useSelector((state) => state.search[type === 'company' ? 'companyFilter' : 'filter'].exclude_linkedin)
  const countIncluded = manualInclude.split('\n').filter(Boolean).length
  const countExcluded = manualExclude.split('\n').filter(Boolean).length
  const [isExcludeBoxOpen, setIsExcludeBoxOpen] = useState(false)

  const handleIncludedProfiles = useCallback((newValue, updateRedux = true) => {
    // Display username-only in the state used for chips
    setIncludedProfiles(newValue.map(url => extractLinkedInUsername(url)))

    // Send complete URLs to Redux
    if (updateRedux) onChange('linkedin', newValue)
    ref.current.internalStateChange = true

    // Update the manual input field when chips are removed
    if (newValue.length < includedProfiles.length) {
      setManualInclude(newValue.join('\n'))
    }
  }, [onChange, includedProfiles])

  const handleExcludedProfiles = useCallback((newValue, updateRedux = true) => {
    // Display username-only in the state used for chips
    setExcludedProfiles(newValue.map(url => extractLinkedInUsername(url)))

    // Send complete URLs to Redux
    if (updateRedux) onChange('exclude_linkedin', newValue)
    ref.current.internalStateChange = true

    // Update the manual input field when chips are removed
    if (newValue.length < excludedProfiles.length) {
      setManualExclude(newValue.join('\n'))
    }
  }, [onChange, excludedProfiles])

  const clearAll = useCallback(() => {
    handleIncludedProfiles([])
    handleExcludedProfiles([])
    setManualInclude('')
    setManualExclude('')
  }, [handleIncludedProfiles, handleExcludedProfiles])

  useEffect(() => {
    if (ref.current.internalStateChange) {
      ref.current.internalStateChange = false
      return
    }

    // Display username-only in the chips
    setIncludedProfiles((linkedinData ?? []).map(url => extractLinkedInUsername(url)))
    setExcludedProfiles((excludeLinkedinData ?? []).map(url => extractLinkedInUsername(url)))

    // Display complete URLs in the text fields
    if (linkedinData?.length) {
      setManualInclude(linkedinData.join('\n'))
    } else {
      setManualInclude('')
    }

    if (excludeLinkedinData?.length) {
      setManualExclude(excludeLinkedinData.join('\n'))
      setIsExcludeBoxOpen(true)
    } else {
      setManualExclude('')
      setIsExcludeBoxOpen(excludeLinkedinData?.length > 0)
    }
  }, [linkedinData, excludeLinkedinData])

  const extractLinkedInUsername = (profile) => {
    return profile
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\/(www\.)?linkedin\.com\/in\//, '') // Remove http(s)://linkedin.com/in/
      .replace(/\/$/, '') // Remove trailing slash
      .split('?')[0] // Remove query parameters
  }

  const formatLinkedInURL = (profile) => {
    let url = profile.trim()

    // Check if the URL starts with http:// or https://
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      // Check if it has www.
      if (url.startsWith('www.')) {
        url = 'http://' + url
      } else if (url.startsWith('linkedin.com/')) {
        url = 'http://www.' + url
      } else if (url.includes('linkedin.com/')) {
        // Handle cases like "in.linkedin.com/in/"
        url = 'http://' + url
      } else {
        // If it's just a username, construct full URL
        url = 'http://www.linkedin.com/in/' + url
      }
    }

    return url
  }

  useEffect(() => {
    if (manualInclude || manualExclude) {
      const includeList = manualInclude.split('\n')
        .filter(Boolean)
        .map(formatLinkedInURL)

      const excludeList = manualExclude.split('\n')
        .filter(Boolean)
        .map(formatLinkedInURL)

      if (includeList.length) {
        handleIncludedProfiles(includeList)
      }

      if (excludeList.length) {
        handleExcludedProfiles(excludeList)
      }
    }
  }, [manualInclude, manualExclude, handleIncludedProfiles, handleExcludedProfiles])

  const preview = (
    <>
      {(includedProfiles.length > 0 || excludedProfiles.length > 0) &&
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
          {includedProfiles.length > 0 &&
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              <Box sx={{ width: '95%', height: '0px', border: '0.5px solid', borderColor: '#E4E4E5', mx: 'auto' }} />
              <Typography variant='body2' sx={{ color: '#8181B0', fontSize: '12px', fontWeight: 600, paddingLeft: 1 }}>Include</Typography>
              {includedProfiles.map((profile, index) => <Tag key={index} label={profile} onDelete={() => handleIncludedProfiles(includedProfiles.filter(p => !isEqual(p, profile)))} />)}
            </Box>}
          {excludedProfiles.length > 0 &&
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              <Box sx={{ width: '95%', height: '0px', border: '0.5px solid', borderColor: '#E4E4E5', mx: 'auto' }} />
              <Typography variant='body2' sx={{ color: '#8181B0', fontSize: '12px', fontWeight: 600, paddingLeft: 1 }}>Exclude</Typography>
              {excludedProfiles.map((profile, index) => <Tag key={index} label={profile} onDelete={() => handleExcludedProfiles(excludedProfiles.filter(p => !isEqual(p, profile)))} isExclude />)}
            </Box>}
        </Box>}
    </>
  )

  return (
    <FilterDropdown
      title='LinkedIn Profiles'
      filterCount={includedProfiles.length + excludedProfiles.length}
      clearAll={clearAll}
      icon={<LinkedIn sx={{ stroke: `${theme.palette.primary.main}` }} />}
      preview={preview}
      isOpen={isOpen}
      onToggle={onToggle}
    >
      <ManualImport count={countIncluded} value={manualInclude} setValue={setManualInclude} heading='Include LinkedIn profiles' />
      {/* Exclude LinkedIn Profiles Section */}
      <Box sx={{ mt: 2, mx: '10px' }}>
        <Button
          endIcon={isExcludeBoxOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            fontSize: '13px',
            p: 0,
            minWidth: 0,
            mb: 1
          }}
          onClick={() => {
            setIsExcludeBoxOpen(!isExcludeBoxOpen)
          }}
        >
          Exclude LinkedIn Profiles
        </Button>
      </Box>

      {isExcludeBoxOpen && (
        <ManualImport count={countExcluded} value={manualExclude} setValue={setManualExclude} heading='Exclude LinkedIn profiles' />
      )}
    </FilterDropdown>
  )
}

function ManualImport ({ count, value, setValue, heading }) {
  return (
    <Box sx={{ marginBottom: 2, mx: '10px' }}>
      <Box sx={{ display: 'flex', alignItem: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography
            variant='subtitle1'
            sx={{ display: 'flex', alignItems: 'center', fontWeight: 500, fontSize: '14px' }}
          >
            {heading}
            <Tooltip
              title='Paste the LinkedIn profile URLs directly into the input field. Separate each entry with a line break.'
              arrow
            >
              <IconButton size='small' sx={{ marginLeft: 0.5 }}>
                <InfoOutlinedIcon fontSize='16px' />
              </IconButton>
            </Tooltip>
          </Typography>
        </Box>
        {/* {count
          ? (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography
                variant='body2'
                color='primary'
                sx={{ cursor: 'pointer', marginRight: 0.5 }}
                onClick={() => setValue('')}
              >
                Clear ({count})
              </Typography>
            </Box>
            )
          : null} */}
      </Box>
      <TextField
        multiline
        minRows={4}
        placeholder='e.g.\nhttps://linkedin.com/in/johndoe\nhttps://www.linkedin.com/in/janedoe\nlinkedin.com/in/username\njsmith'
        value={value}
        onChange={(e) => setValue(e.target.value)}
        fullWidth
        sx={{ marginTop: 1, border: ' 1px solid #DCDEE0', borderRadius: '8px' }}
      />
      <Box
        sx={{
          textAlign: 'right',
          color: 'text.secondary',
          fontSize: '0.875rem',
          marginTop: 0.5
        }}
      >
        {count}
      </Box>
    </Box>
  )
}
